import { StyleSheet, Platform } from 'react-native'
import type { Theme } from '../../../src/theme/getTheme'

export const landingFeatureVisualGlowStart = { x: 0, y: 0 }
export const landingFeatureVisualGlowEnd = { x: 1, y: 1 }

export function getLandingFeaturesStyles(theme: Theme) {
  return StyleSheet.create({
  section: {
    paddingHorizontal: 24,
    paddingVertical: 56,
    overflow: 'hidden',
  },
  sectionPhone: {
    paddingHorizontal: 16,
    paddingVertical: 40,
  },
  container: {
    maxWidth: 1240,
    marginHorizontal: 'auto' as unknown as number,
    width: '100%',
  },
  sectionHead: {
    alignItems: 'center',
    marginBottom: 48,
    maxWidth: 720,
    alignSelf: 'center',
  },
  sectionHeadPhone: {
    marginBottom: 30,
  },
  eyebrow: {
    fontFamily:
      Platform.OS === 'web' ? '"JetBrains Mono", ui-monospace, monospace' : undefined,
    fontSize: 12,
    fontWeight: '600',
    letterSpacing: 0.5,
    color: theme.primary,
    textTransform: 'uppercase',
    marginBottom: 12,
  },
  h2: {
    fontFamily:
      Platform.OS === 'web'
        ? '"Space Grotesk", "Inter", system-ui, sans-serif'
        : undefined,
    fontSize: Platform.OS === 'web' ? 44 : 28,
    fontWeight: '700',
    lineHeight: Platform.OS === 'web' ? 50 : 34,
    letterSpacing: -1.2,
    color: theme.gray[900],
    textAlign: 'center',
    marginBottom: 16,
  },
  h2Phone: {
    fontSize: 30,
    lineHeight: 36,
    letterSpacing: -0.8,
  },
  p: {
    fontFamily: Platform.OS === 'web' ? '"Inter", system-ui, sans-serif' : undefined,
    fontSize: 17,
    lineHeight: 26,
    color: theme.gray[600],
    textAlign: 'center',
  },
  pPhone: {
    fontSize: 15,
    lineHeight: 23,
  },
  showcase: {
    flexDirection: Platform.OS === 'web' ? 'row' : 'column',
    alignItems: 'center',
    gap: 44,
  },
  showcaseStacked: {
    alignItems: 'stretch',
    gap: 28,
  },
  showcasePhone: {
    gap: 22,
  },
  featureRail: {
    width: Platform.OS === 'web' ? ('42%' as unknown as number) : '100%',
    flexShrink: 0,
  },
  featureRailStacked: {
    width: '100%',
  },
  featureRailContent: {
    gap: 14,
  },
  featureRailContentStacked: {
    gap: 14,
    paddingHorizontal: 2,
    paddingBottom: 8,
  },
  featureCard: {
    backgroundColor: theme.background.card,
    borderRadius: 24,
    padding: 20,
    borderWidth: 1,
    borderColor: theme.gray[300],
    shadowColor: theme.primaryDark,
    shadowOffset: { width: 0, height: 14 },
    shadowOpacity: 0.05,
    shadowRadius: 26,
    elevation: 2,
    overflow: 'hidden',
  },
  featureCardStacked: {
    width: 274,
    minHeight: 252,
  },
  featureCardActive: {
    borderColor: theme.primary + '66',
    shadowOpacity: 0.16,
    transform: [{ translateY: -2 }],
  },
  featureCardHovered: {
    transform: [{ translateY: -3 }],
  },
  featureCardPressed: {
    opacity: 0.84,
    transform: [{ translateY: 0 }],
  },
  featureCardTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 12,
  },
  featureHeadingLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
  },
  featureIcon: {
    width: 46,
    height: 46,
    borderRadius: 16,
    backgroundColor: theme.primary + '1A',
    alignItems: 'center',
    justifyContent: 'center',
  },
  featureIconActive: {
    backgroundColor: theme.primary,
  },
  featureIndexPill: {
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 5,
    backgroundColor: theme.background.primary,
    borderWidth: 1,
    borderColor: theme.primaryLight,
  },
  featureIndexText: {
    fontFamily:
      Platform.OS === 'web' ? '"JetBrains Mono", ui-monospace, monospace' : undefined,
    color: theme.primaryLight,
    fontSize: 12,
    fontWeight: '700',
  },
  featureTitle: {
    fontFamily:
      Platform.OS === 'web'
        ? '"Space Grotesk", "Inter", system-ui, sans-serif'
        : undefined,
    fontSize: 19,
    fontWeight: '700',
    letterSpacing: -0.5,
    color: theme.gray[800],
    flex: 1,
  },
  featureTitleActive: {
    color: theme.gray[900],
  },
  featureBody: {
    fontFamily: Platform.OS === 'web' ? '"Inter", system-ui, sans-serif' : undefined,
    fontSize: 15,
    lineHeight: 22,
    color: theme.gray[500],
  },
  featureBodyActive: {
    color: theme.gray[600],
  },
  progressTrack: {
    height: 4,
    borderRadius: 999,
    backgroundColor: theme.gray[200],
    marginTop: 18,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 999,
    backgroundColor: theme.primary,
  },
  visualColumn: {
    width: Platform.OS === 'web' ? ('58%' as unknown as number) : '100%',
    maxWidth: 650,
    minHeight: Platform.OS === 'web' ? 560 : 420,
    justifyContent: 'center',
  },
  visualColumnStacked: {
    width: '100%',
    maxWidth: 680,
    alignSelf: 'center',
  },
  visualColumnPhone: {
    minHeight: 360,
  },
  visualGlow: {
    position: 'absolute',
    top: 28,
    right: 8,
    bottom: 22,
    left: 28,
    borderRadius: 42,
    transform: [{ rotate: '-4deg' }],
  },
  visualFrame: {
    borderRadius: 36,
    padding: 14,
    backgroundColor: theme.background.card + 'CC',
    borderWidth: 1,
    borderColor: theme.gray[300],
    shadowColor: theme.primaryDark,
    shadowOffset: { width: 0, height: 30 },
    shadowOpacity: 0.18,
    shadowRadius: 46,
    elevation: 5,
    overflow: 'hidden',
  },
  visualFramePhone: {
    borderRadius: 24,
    padding: 10,
  },
  visualAnimated: {
    borderRadius: 26,
    overflow: 'hidden',
    backgroundColor: theme.background.shell,
  },
  visualImage: {
    width: '100%',
    height: Platform.OS === 'web' ? 460 : 360,
  },
  visualImagePhone: {
    height: 300,
  },
  visualCaption: {
    position: 'absolute',
    right: 26,
    bottom: 18,
    maxWidth: 260,
    borderRadius: 22,
    paddingHorizontal: 20,
    paddingVertical: 16,
    backgroundColor: theme.background.inset + 'E0',
    borderWidth: 1,
    borderColor: theme.gray[300],
  },
  visualCaptionPhone: {
    right: 12,
    bottom: 10,
    maxWidth: 220,
    borderRadius: 18,
    paddingHorizontal: 14,
    paddingVertical: 12,
  },
  visualCaptionKicker: {
    fontFamily:
      Platform.OS === 'web' ? '"JetBrains Mono", ui-monospace, monospace' : undefined,
    color: theme.accent,
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 0.6,
    marginBottom: 6,
    textTransform: 'uppercase',
  },
  visualCaptionTitle: {
    fontFamily:
      Platform.OS === 'web'
        ? '"Space Grotesk", "Inter", system-ui, sans-serif'
        : undefined,
    color: '#FFFFFF',
    fontSize: 18,
    fontWeight: '700',
    lineHeight: 23,
  },
  })
}
